<template>
    <re-b-modal ref="modal" large hide-footer no-close-on-backdrop
                :header="$t('title.em.asset.emArchFixedAsset.import')"
                @hide="reset">
        <div class="d-flex flex-column w-100">
            <b-form-file v-model="file"
                         class="w-100"
                         accept=".xls,.xlsx"
                         :placeholder="$t('label.em.asset.emArchFixedAsset.filePlaceholder')"
                         :browse-text="$t('label.em.asset.emArchFixedAsset.browse')"></b-form-file>

            <b-button-group class="mt-2 align-self-start">
                <mini-button name="downloadTemplate" :loading="downloading" @btnClick="downloadTemplate">
                    <i class="fa fa-fw fa-download"></i>
                    <span class="ml-1">{{ $t('label.em.asset.emArchFixedAsset.downloadTemplate') }}</span>
                </mini-button>
                <mini-button name="doImport" :loading="importing" @btnClick="doImport">
                    <i class="fa-solid fa-file-import"></i>
                    <span class="ml-1">{{ $t('label.em.asset.emArchFixedAsset.doImport') }}</span>
                </mini-button>
            </b-button-group>

            <b-alert :show="!!hintMessage" :variant="hintVariant" class="mt-2 py-2 mb-0">
                {{ hintMessage }}
            </b-alert>

            <b-table v-if="errors.length > 0"
                     class="mt-2 mb-0"
                     small striped bordered responsive
                     :items="errors"
                     :fields="errorFields"></b-table>
        </div>
    </re-b-modal>
</template>

<script>
import ReBModal from "@/components/echarts-part/CalendarShift/cells/stuffs/ReBModal.vue";
import MiniButton from "@/components/common/buttons/MiniButton.vue";
import emCommonUtilMixin from "@/mixin/em/common/stuffs/utils/emCommonUtilMixin";

/**
 * 固定资产档案 Excel 导入弹窗（验收 #4）
 * <p>
 * 后端是「整批」语义：任一行校验不过就一行都不写，并在 200 响应里返回 errors=[{row, message}]，
 * 所以导入失败不会走 store/api.js 的全局错误弹窗，必须在这里把错误表格显示出来。
 */
export default {
    name: "EmArchFixedAssetImportModal",
    components: {MiniButton, ReBModal},
    mixins: [emCommonUtilMixin],
    data() {
        return {
            file: null,
            downloading: false,
            importing: false,
            hintMessage: '',
            hintVariant: 'success',
            errors: [],
        }
    },
    computed: {
        baseApi() {
            return process.env.VUE_APP_EM_ARCHIVE_URL + '/emArchFixedAsset';
        },
        errorFields() {
            return [
                {key: 'row', label: this.$t('label.em.asset.emArchFixedAsset.errorRow'), thStyle: {width: '90px'}},
                {key: 'message', label: this.$t('label.em.asset.emArchFixedAsset.errorMessage')},
            ];
        },
    },
    methods: {
        open() {
            this.reset();
            this.$refs.modal.showModal();
        },

        reset() {
            this.file = null;
            this.errors = [];
            this.hintMessage = '';
            this.hintVariant = 'success';
        },

        downloadTemplate() {
            this.downloading = true;
            this.downloadWithHeader(
                    `${this.baseApi}/importTemplate`,
                    this.$t('label.em.asset.emArchFixedAsset.templateFileName'),
                    (success, errorMsg) => {
                        this.downloading = false;
                        if (!success) {
                            this.showHint('danger',
                                    errorMsg || this.$t('label.em.asset.emArchFixedAsset.downloadFailed'));
                        }
                    });
        },

        doImport() {
            if (!this.file) {
                this.showHint('warning', this.$t('label.em.asset.emArchFixedAsset.noFileChosen'));
                return;
            }
            let param = new FormData();
            param.append('file', this.file);
            this.importing = true;
            this.errors = [];
            this.hintMessage = '';
            // api/postMultipart 解析出来的是完整 response，业务体在 .data（EmResponseUtils.success 裸透传）
            this.$store.dispatch('api/postMultipart', {url: `${this.baseApi}/import`, data: param})
                    .then(response => {
                        let result = response && response.data;
                        if (!result) {
                            return;
                        }
                        this.errors = result.errors || [];
                        if (result.success) {
                            this.$bvToast.toast(this.importedText(result), {
                                variant: 'success',
                                noCloseButton: true,
                                autoHideDelay: 3000
                            });
                            this.$emit('imported');
                            this.$refs.modal.hide();
                        } else {
                            this.showHint('danger',
                                    `${this.$t('label.em.asset.emArchFixedAsset.importRejected')}（${this.errors.length}）`);
                        }
                    })
                    .catch(() => {
                        // 读文件失败 / 非财务等由 store/api.js 的 error action 统一弹窗，这里只收尾
                    })
                    .finally(() => this.importing = false);
        },

        importedText(result) {
            return `${this.$t('label.em.asset.emArchFixedAsset.importSuccess')}`
                    + `（${this.$t('label.em.asset.emArchFixedAsset.created')} ${result.created || 0} / `
                    + `${this.$t('label.em.asset.emArchFixedAsset.updated')} ${result.updated || 0}）`;
        },

        showHint(variant, message) {
            this.hintVariant = variant;
            this.hintMessage = message;
        },
    }
}
</script>

<style scoped>
.w-100 >>> .custom-file-label {
    width: 100%;
}
</style>
